import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CompanySettings {
  razao_social?: string;
  cnpj?: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_estado?: string;
  endereco_cep?: string;
  telefone?: string;
  email?: string;
  logo_url?: string;
}

export function useCompanySettings() {
  return useQuery({
    queryKey: ['company-settings'],
    queryFn: async (): Promise<CompanySettings> => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .like('key', 'company_%');

      if (error) throw error;

      const settings: CompanySettings = {};
      data?.forEach((item) => {
        const key = item.key.replace('company_', '') as keyof CompanySettings;
        settings[key] = item.value;
      });

      return settings;
    },
  });
}

export function useUpdateCompanySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: CompanySettings) => {
      // Executar upserts e capturar todos os resultados
      const results = await Promise.all(
        Object.entries(settings).map(([key, value]) => 
          supabase
            .from('system_settings')
            .upsert({
              key: `company_${key}`,
              value: value,
              description: `Company ${key} setting`,
            }, {
              onConflict: 'key' // Especificar coluna de conflito
            })
        )
      );

      // Verificar se houve algum erro
      const errors = results.filter(result => result.error);
      
      if (errors.length > 0) {
        console.error('Erros ao salvar configurações:', errors);
        throw new Error(
          `Falha ao salvar ${errors.length} configuração(ões): ${
            errors.map(e => e.error?.message).join(', ')
          }`
        );
      }

      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
    },
  });
}